import { create } from 'zustand';
import { TenantService } from 'src/services/shared/tenant.service';
import { AccountService } from 'src/services/shared/account.service';
import { Account } from 'src/models/Account';
import environment from 'src/settings/environmentLoader';

// Types
interface AccountState {
    accounts: Account[];
    isLoading: boolean;
    error: string | null;
    lastError: string | null;

    // Computed values
    hasAnyAccount: boolean;
    accountCount: number;

    // Actions
    loadAccounts: () => Promise<void>;
    setAccounts: (accounts: Account[]) => void;
    createAccount: (account: Account) => Promise<void>;
    updateAccount: (accountId: string, updates: Partial<Account>) => Promise<void>;
    deleteAccount: (accountId: string) => Promise<void>;
    loadRoleStatus: (accountId: string) => Promise<void>;
    clearError: () => void;
    reset: () => void;
}

// Initial state
const initialState = {
    accounts: [],
    isLoading: false,
    error: null,
    lastError: null,
    hasAnyAccount: false,
    accountCount: 0,
};

// Create the store
export const useAccountStore = create<AccountState>((set, get) => ({
    ...initialState,

    // Actions
    loadAccounts: async () => {
        try {
            set({ isLoading: true, error: null });

            const accounts = await TenantService.getAccounts();

            set({
                accounts,
                hasAnyAccount: accounts.length > 0,
                accountCount: accounts.length,
                isLoading: false
            });
        } catch (error: any) {
            console.error('Failed to load accounts:', error);
            const errorMessage = error.message || 'Failed to load accounts';
            set({
                error: errorMessage,
                lastError: errorMessage,
                isLoading: false
            });
        }
    },

    setAccounts: (accounts: Account[]) => {
        set({
            accounts,
            hasAnyAccount: accounts.length > 0,
            accountCount: accounts.length,
            error: null
        });
    },

    createAccount: async (account: Account) => {
        try {
            set({ isLoading: true, error: null });

            // Set default connection values
            const roleName = account.roleName || environment.defaultRoleName;

            await TenantService.createAccount({
                accountId: account.id,
                name: account.name,
                connectionType: account.connectionType || 'assumeRole',
                roleName,
                externalId: account.externalId,
            });

            // Reload from backend to get the authoritative state
            await get().loadAccounts();
        } catch (error: any) {
            console.error('Failed to create account:', error);
            const errorMessage = error.message || 'Failed to create account';
            set({
                error: errorMessage,
                lastError: errorMessage,
                isLoading: false,
            });
            throw error;
        }
    },

    updateAccount: async (accountId: string, updates: Partial<Account>) => {
        try {
            set({ isLoading: true, error: null });

            await TenantService.updateAccount({
                accountId,
                name: updates.name,
                connectionType: updates.connectionType,
                roleName: updates.roleName,
                externalId: updates.externalId,
            });

            // Reload from backend to get the authoritative state
            await get().loadAccounts();
        } catch (error: any) {
            console.error('Failed to update account:', error);
            const errorMessage = error.message || 'Failed to update account';
            set({
                error: errorMessage,
                lastError: errorMessage,
                isLoading: false,
            });
            throw error;
        }
    },

    deleteAccount: async (accountId: string) => {
        try {
            set({ isLoading: true, error: null });

            await TenantService.deleteAccount(accountId);

            // Reload from backend to get the authoritative state
            await get().loadAccounts();
        } catch (error: any) {
            console.error('Failed to delete account:', error);
            const errorMessage = error.message || 'Failed to delete account';
            set({
                error: errorMessage,
                lastError: errorMessage,
                isLoading: false,
            });
            throw error;
        }
    },

    loadRoleStatus: async (accountId: string) => {
        try {
            const response = await AccountService.loadRoleStatusByAccountId(accountId);
            const { accounts } = get();
            const updatedAccounts = accounts.map(account =>
                account.id === accountId
                    ? {
                        ...account,
                        roleStatus: response.roleStatus,
                        connectionStatus: response.connectionStatus,
                        roleVersion: response.roleVersion,
                    }
                    : account
            );
            set({ accounts: updatedAccounts });
        } catch (error: any) {
            console.error('Failed to load role status:', error);
            throw error;
        }
    },

    clearError: () => {
        set({ error: null, lastError: null });
    },

    reset: () => {
        set(initialState);
    }
}));

// Selectors for better performance
export const useAccountList = () => useAccountStore(state => state.accounts);
export const useHasAnyAccount = () => useAccountStore(state => state.hasAnyAccount);
export const useAccountCount = () => useAccountStore(state => state.accountCount);
export const useAccountLoading = () => useAccountStore(state => state.isLoading);
export const useAccountError = () => useAccountStore(state => state.error);

export default useAccountStore;
